import {matchRoutes, RouteObject} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import * as routeConstants from '../constants/routes';

const SPLAT_PARAM_KEY = '*';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && isNotNil(value);

// Recursively collect every route template ('/'-prefixed string) declared in the route constants.
const collectRouteTemplates = (value: unknown, templates: Set<string>): void => {
  if (typeof value === 'string') {
    if (value.startsWith('/')) {
      templates.add(value);
    }
    return;
  }
  if (isRecord(value)) {
    Object.values(value).forEach((entry) => collectRouteTemplates(entry, templates));
  }
};

// Built once: a flat catalog of every @dms/routes template. matchRoutes applies React Router's
// specificity ranking, so a static segment wins over a `:param` when both could match a pathname.
const buildRouteCatalog = (): RouteObject[] => {
  const templates = new Set<string>();
  collectRouteTemplates(routeConstants, templates);
  return Array.from(templates, (path) => ({path}));
};

const ROUTE_CATALOG = buildRouteCatalog();

/**
 * Resolves the dynamic route path params (`:id` segments) for a pathname against the full
 * @dms/routes catalog. Returns `{}` when the route has no dynamic segments (e.g. `/vehicles`).
 * The splat (`*`) param is excluded.
 */
export const getRouteParams = (pathname: string): Record<string, string> => {
  const matches = matchRoutes(ROUTE_CATALOG, pathname);
  const matchedParams = matches?.[matches.length - 1]?.params;

  if (isNil(matchedParams)) {
    return {};
  }

  const params: Record<string, string> = {};
  Object.entries(matchedParams).forEach(([key, value]) => {
    if (key !== SPLAT_PARAM_KEY && isNotNil(value)) {
      params[key] = value;
    }
  });
  return params;
};
